import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import SITE from '../api';
import { usePortal, PORTAL } from '../portalAuth';
import { useSite, IMG } from '../components/StoreLayout';
import { ProductCard, isGoldPriced } from './Home';
import StorePhoneInput from '../components/StorePhoneInput';
import Captcha from '../components/Captcha';
import useSEO from '../useSEO';
import { trackEvent } from '../analytics';
import { STORE_BASE, STORE_HOME } from '../routeBase';

function ytVideoId(url) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{6,})/);
  return m ? m[1] : '';
}

export default function Product() {
  const { model } = useParams();
  const { content: c } = useSite();
  const portal = usePortal();
  const customer = portal?.customer;
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [colorIdx, setColorIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);
  // qty per colour (keyed by variant_id) — lets a customer set quantities for
  // several colours at once and add them all in a single click. Previously
  // there was one shared qty tied to whichever colour was selected, so
  // switching colours silently dropped the quantity set for the first one
  // (it was never actually submitted — only the colour active at the moment
  // "Add to cart" was clicked went through).
  const [qtyByColor, setQtyByColor] = useState({});
  const [adding, setAdding] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [enq, setEnq] = useState({ name: '', email: '', phone: '', message: '' });
  const [enqSent, setEnqSent] = useState(false);
  const [enqBusy, setEnqBusy] = useState(false);
  const [enqErr, setEnqErr] = useState('');
  const [enqCaptcha, setEnqCaptcha] = useState('');

  useEffect(() => {
    setP(null); setColorIdx(0); setImgIdx(0); setQtyByColor({}); setNotFound(false);
    // logged-in customers get priced data from the portal API; guests get public data
    const client = customer ? PORTAL : SITE;
    client.get(`/product/${encodeURIComponent(model)}`)
      .then(r => {
        setP(r.data);
        if (r.data.colors?.[0]) setQtyByColor({ [r.data.colors[0].id]: 1 });
        trackEvent('product_view', {
          item_id: r.data.model_no, item_name: r.data.title || r.data.model_no,
          price: r.data.colors?.[0]?.price ?? undefined, currency: r.data.currency || 'USD',
        });
      })
      .catch(() => setNotFound(true));
    // same client choice as the main product fetch above — without this,
    // related cards never got a price even when logged in, since the public
    // endpoint has no concept of a customer's price tier.
    client.get(`/product/${encodeURIComponent(model)}/related`).then(r => setRelated(r.data.products || []));
  }, [model, customer]);

  useSEO({
    title: p?.seo?.title || p?.title,
    description: p?.seo?.description,
    keywords: p?.seo?.keywords,
    image: p?.colors?.[0]?.images?.[0] ? `${window.location.origin}/product-images/${p.colors[0].images[0]}` : undefined,
    canonical: p ? `${window.location.origin}/store/product/${encodeURIComponent(p.slug || p.model_no)}` : undefined,
  });

  const bumpQty = (variantId, delta) => setQtyByColor(q => ({ ...q, [variantId]: Math.max(0, (q[variantId] || 0) + delta) }));
  const setQtyDirect = (variantId, val) => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    setQtyByColor(q => ({ ...q, [variantId]: n }));
  };
  const totalSelectedQty = Object.values(qtyByColor).reduce((a, b) => a + b, 0);

  // Adds every colour with a qty > 0 in one go, so nothing set earlier gets
  // silently dropped when moving on to the next colour.
  const addToCart = async () => {
    if (!customer) { nav(`${STORE_BASE}/login`); return; }
    const toAdd = Object.entries(qtyByColor).filter(([, q]) => q > 0);
    if (!toAdd.length) return;
    setAdding(true);
    try {
      for (const [variantId, q] of toAdd) {
        await PORTAL.post('/cart', { variant_id: Number(variantId), qty: q });
      }
      await portal.refreshCart();
      const items = toAdd.map(([variantId, q]) => {
        const c = p.colors.find(col => String(col.id) === String(variantId));
        return { item_id: p.model_no, item_name: p.title || p.model_no, item_variant: c?.color, price: c?.price, quantity: q };
      });
      trackEvent('add_to_cart', {
        currency: p.currency || 'USD',
        value: items.reduce((sum, it) => sum + (it.price || 0) * it.quantity, 0),
        items,
      });
      setQtyByColor({});
    } catch (e) { alert(e.response?.data?.error || 'Could not add to cart'); }
    setAdding(false);
  };

  // Public "ask about this product" enquiry — no login required. Same
  // /enquiry endpoint the Contact page uses, tagged source:'product' so
  // admins can tell product enquiries apart from general contact messages.
  const submitEnquiry = async e => {
    e.preventDefault(); setEnqErr('');
    if (!enq.name || !enq.name.trim()) return setEnqErr('Please enter your name');
    if (!enq.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(enq.email.trim())) return setEnqErr('Please enter a valid email address');
    if (!enq.phone || !enq.phone.trim()) return setEnqErr('Please enter your phone number');
    if (!enq.message) return setEnqErr('Please enter a message');
    setEnqBusy(true);
    try {
      await SITE.post('/enquiry', { ...enq, subject: `Product enquiry: ${p.model_no}`, source: 'product', model_no: p.model_no, captcha_token: enqCaptcha });
      setEnqSent(true);
      trackEvent('wholesale_enquiry', { item_id: p.model_no, item_name: p.title || p.model_no, source: 'product' });
    } catch (e2) { setEnqErr(e2.response?.data?.error || 'Could not send'); }
    setEnqBusy(false);
  };

  if (notFound) return <div style={{ padding: 80, textAlign: 'center', color: '#888' }}>Product not found. <Link to={`${STORE_BASE}/shop`} style={{ color: 'var(--sf-accent)' }}>Back to shop</Link></div>;
  if (!p) return <div style={{ padding: 80, textAlign: 'center', color: '#aaa' }}>Loading…</div>;

  const color = p.colors[colorIdx] || p.colors[0];
  const inWishlist = portal?.wishlist?.items?.some(it => it.variant_id === color?.id);
  const inCompare = portal?.compare?.items?.some(it => it.variant_id === color?.id);
  const toggleWishlist = async () => {
    if (!customer) { nav(`${STORE_BASE}/login`); return; }
    try {
      const r = inWishlist ? await PORTAL.delete(`/wishlist/${color.id}`) : await PORTAL.post('/wishlist', { variant_id: color.id });
      portal.setWishlist(r.data);
    } catch (e) { alert(e.response?.data?.error || 'Could not update wish list'); }
  };
  const toggleCompare = async () => {
    if (!customer) { nav(`${STORE_BASE}/login`); return; }
    try {
      const r = inCompare ? await PORTAL.delete(`/compare/${color.id}`) : await PORTAL.post('/compare', { variant_id: color.id });
      portal.setCompare(r.data);
    } catch (e) { alert(e.response?.data?.error || 'Could not update compare list'); }
  };
  const images = color?.images?.length ? color.images : [];
  const mainImg = images[imgIdx];
  const s = p.specs;

  // Hover-to-zoom on the main product image — the cursor position within the
  // frame sets the zoomed image's transform-origin, so the zoom follows it.
  const handleZoomMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.querySelector('img').style.transformOrigin = `${x}% ${y}%`;
  };

  return (
    <>
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      {/* breadcrumb */}
      <div style={{ fontSize: 12.5, color: '#999', marginBottom: 18 }}>
        <Link to={STORE_HOME} style={bc}>Home</Link> ›{' '}
        <Link to={`${STORE_BASE}/shop?category=${encodeURIComponent(p.category)}`} style={bc}>{p.category}</Link> ›{' '}
        <span style={{ color: '#555' }}>{p.model_no}</span>
      </div>

      <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* gallery */}
        <div style={{ flex: '1 1 380px', maxWidth: 460 }}>
          <div onMouseMove={mainImg ? handleZoomMove : undefined}
            style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, height: 380,
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, overflow: 'hidden',
            cursor: mainImg ? 'zoom-in' : 'default' }}
            className={mainImg ? 'sf-zoom-box' : undefined}>
            {mainImg
              ? <img src={IMG(mainImg)} alt={`${[p.brand, p.gender, p.shape, p.material, p.frame_type].filter(Boolean).join(' ')} eyeglass frame — model ${p.model_no}`.trim()} className="sf-zoom-img" style={{ maxWidth: '92%', maxHeight: '92%', objectFit: 'contain' }}
                  onError={e => { e.target.style.display = 'none'; }} />
              : <i className="ti ti-eyeglass-2" style={{ fontSize: 70, color: '#ddd' }} />}
          </div>
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {images.map((im, i) => (
                <div key={i} onClick={() => setImgIdx(i)} style={{ width: 62, height: 62, borderRadius: 8, cursor: 'pointer',
                  border: i === imgIdx ? '2px solid var(--sf-primary)' : '1px solid #eee', background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <img src={IMG(im)} alt="" loading="lazy" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>Colours shown are for reference only and may vary slightly from the actual product</p>

          {/* share — real share-intent URLs, styled with the site's own
              circular icon-button look (sf-social-icon colors) rather than
              each network's own brand color, per house style. Sits between
              the gallery and the enquiry box, with breathing room on both
              sides. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0' }}>
            <span style={{ fontSize: 12.5, color: '#888' }}>Share:</span>
            {[
              { icon: 'ti-brand-whatsapp', label: 'Share on WhatsApp',
                url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${p.model_no} — ${window.location.href}`)}` },
              { icon: 'ti-brand-facebook', label: 'Share on Facebook',
                url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
              { icon: 'ti-brand-x', label: 'Share on Twitter',
                url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(p.model_no)}` },
            ].map(s => (
              <a key={s.icon} href={s.url} target="_blank" rel="noopener noreferrer" title={s.label}
                className="sf-social-icon"
                style={{ width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                <i className={`ti ${s.icon}`} style={{ fontSize: 15 }} />
              </a>
            ))}
          </div>

          {/* ask about this product — public, no login required. Lives under
              the gallery (not the long info column) so the two columns come
              out roughly the same height instead of the page trailing off
              in one tall column with empty space beside it. */}
          <div style={{ marginTop: 20, background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: 18 }}>
            {enqSent ? (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <i className="ti ti-circle-check" style={{ fontSize: 32, color: '#16a34a' }} />
                <p style={{ color: '#666', fontSize: 13.5, marginTop: 8 }}>Thanks — your enquiry about {p.model_no} has been sent. We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={submitEnquiry}>
                <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 12 }}>Ask about this product</div>
                {enqErr && <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 12.5, padding: '7px 10px', borderRadius: 8, marginBottom: 10 }}>{enqErr}</div>}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 140px' }}>
                    <input value={enq.name} onChange={e => setEnq({ ...enq, name: e.target.value })} placeholder="Your name *"
                      style={enqInp} required />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <input type="email" value={enq.email} onChange={e => setEnq({ ...enq, email: e.target.value })} placeholder="Email *"
                      style={enqInp} required />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <StorePhoneInput value={enq.phone} onChange={v => setEnq({ ...enq, phone: v })} required />
                  </div>
                </div>
                <textarea value={enq.message} onChange={e => setEnq({ ...enq, message: e.target.value })} rows={3}
                  placeholder={`I'd like to know more about ${p.model_no}…`}
                  style={{ ...enqInp, width: '100%', resize: 'vertical', marginTop: 10, boxSizing: 'border-box' }} />
                <Captcha onToken={setEnqCaptcha} />
                <button type="submit" disabled={enqBusy} className="sf-btn-outline"
                  style={{ marginTop: 10, padding: '9px 22px', border: '1.5px solid var(--sf-accent)', borderRadius: 20, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
                  {enqBusy ? 'Sending…' : 'Send enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* info */}
        <div style={{ flex: '1 1 380px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <h1 style={{ fontSize: 30, fontWeight: 700, margin: '0 0 4px' }}>{p.model_no}</h1>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button onClick={toggleWishlist} title={inWishlist ? 'Remove from wish list' : 'Add to wish list'}
                style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid #eee', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className={`ti ${inWishlist ? 'ti-heart-filled' : 'ti-heart'}`} style={{ fontSize: 17, color: inWishlist ? '#e0245e' : '#555' }} />
              </button>
              <button onClick={toggleCompare} title={inCompare ? 'Remove from compare' : 'Add to compare'}
                style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid #eee', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-arrows-right-left" style={{ fontSize: 16, color: inCompare ? 'var(--sf-accent)' : '#555' }} />
              </button>
            </div>
          </div>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>{p.category}</div>

          {/* price when logged in; gate when not */}
          {isGoldPriced(p) ? (
            <div style={{ background: '#fdf8ec', border: '1px solid #ecd9a8', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: 13.5, color: '#6b5518', lineHeight: 1.6 }}>
              Pure Gold pricing follows the international gold market and is updated daily. Please contact our sales team for a live quote based on today's rate.
              {' '}<a href={`https://api.whatsapp.com/send?phone=${c['brand.whatsapp']||''}`} style={{ color: 'var(--sf-accent)', fontWeight: 600 }}>Contact Sales →</a>
            </div>
          ) : customer && p.price != null ? (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--sf-primary)' }}>
                  {p.currency} {p.price}
                  <span style={{ fontSize: 13, color: '#888', fontWeight: 400 }}> / piece</span>
                </div>
                {p.discount_pct > 0 && p.original_price != null && (
                  <>
                    <span style={{ fontSize: 16, textDecoration: 'line-through', color: '#999' }}>{p.currency} {p.original_price}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 5, padding: '3px 8px' }}>
                      {p.discount_pct}% OFF
                    </span>
                  </>
                )}
              </div>
            </div>
          ) : customer ? (
            <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: 13, color: '#666' }}>
              Price on request for this item. <a href={`https://api.whatsapp.com/send?phone=${c['brand.whatsapp']||''}`} style={{ color: 'var(--sf-accent)' }}>Contact us</a>
            </div>
          ) : (
            <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
              <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>Please register or login to view wholesale prices</div>
              <div style={{ display: 'flex', gap: 10 }}>
                <Link to={`${STORE_BASE}/login`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '9px 22px', borderRadius: 22, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Login to view price</Link>
                <Link to={`${STORE_BASE}/register`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '9px 22px', borderRadius: 22, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>Register</Link>
              </div>
            </div>
          )}

          {/* colour + quantity — every colour has its own qty input, so
              setting quantities across several colours before one "Add to
              cart" click adds all of them, not just whichever was selected
              last. */}
          {customer && p.price != null && !isGoldPriced(p) && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4, color: '#555' }}>
                {p.colors.length > 1 ? `Select Colour & Quantity (${p.colors.length})` : 'Quantity'}
              </div>
              <div style={{ border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' }}>
                {p.colors.map((cl, i) => (
                  <div key={cl.id} onClick={() => { setColorIdx(i); setImgIdx(0); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer',
                      borderBottom: i < p.colors.length - 1 ? '1px solid #f2f2f2' : 'none',
                      background: i === colorIdx ? '#fafafa' : '#fff' }}>
                    <div style={{
                      width: 40, height: 40, flexShrink: 0, borderRadius: 8, overflow: 'hidden', background: '#fff',
                      border: i === colorIdx ? '2px solid var(--sf-accent)' : '1px solid #e2e2e2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {cl.images?.[0]
                        ? <img src={IMG(cl.images[0])} alt={cl.color} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        : <i className="ti ti-eyeglass-2" style={{ fontSize: 16, color: '#ddd' }} />}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: i === colorIdx ? 600 : 400, color: i === colorIdx ? 'var(--sf-primary)' : '#444' }}>
                      {cl.color} {cl.size && `· ${cl.size}mm`}
                    </span>
                    {cl.stock != null && (
                      <span style={{ fontSize: 11.5, color: cl.stock > 0 ? '#16a34a' : '#c0392b' }}>
                        {cl.stock > 0 ? `${cl.stock} in stock` : 'Out of stock'}
                      </span>
                    )}
                    <div onClick={e => e.stopPropagation()} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, border: '1px solid #ddd', borderRadius: 8, padding: '2px 4px', flexShrink: 0 }}>
                      <button onClick={() => bumpQty(cl.id, -1)} style={{ width: 24, height: 24, border: 'none', background: 'none', cursor: 'pointer', fontSize: 16 }}>−</button>
                      <input type="number" min="0" value={qtyByColor[cl.id] || 0}
                        onChange={e => setQtyDirect(cl.id, e.target.value)}
                        style={{ width: 40, textAlign: 'center', border: 'none', fontSize: 13.5, MozAppearance: 'textfield' }} />
                      <button onClick={() => bumpQty(cl.id, 1)} style={{ width: 24, height: 24, border: 'none', background: 'none', cursor: 'pointer', fontSize: 16 }}>+</button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addToCart} disabled={adding || totalSelectedQty === 0} className="sf-btn-outline"
                style={{ marginTop: 12, border: '1.5px solid var(--sf-accent)', padding: '11px 28px', borderRadius: 24, fontSize: 14, fontWeight: 600, cursor: totalSelectedQty === 0 ? 'default' : 'pointer', opacity: totalSelectedQty === 0 ? 0.5 : 1 }}>
                <i className="ti ti-shopping-bag" /> {adding ? 'Adding…' : `Add to cart${totalSelectedQty > 0 ? ` (${totalSelectedQty})` : ''}`}
              </button>
            </div>
          )}

          {p.description && <p style={{ fontSize: 13.5, lineHeight: 1.7, color: '#555', marginBottom: 18 }}>{p.description}</p>}

          {ytVideoId(p.youtube_url) && (
            <div style={{ width: 480, maxWidth: '100%', height: 270, margin: '0 0 18px', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
              <iframe
                key={p.youtube_url}
                src={`https://www.youtube.com/embed/${ytVideoId(p.youtube_url)}?autoplay=1&mute=1&loop=1&playlist=${ytVideoId(p.youtube_url)}&controls=1&rel=0&modestbranding=1`}
                title={`${p.model_no} video`}
                style={{ width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          )}

          {/* measurement diagram + spec table share one card, so the page
              doesn't read as two loosely-related floating blocks */}
          <Specifications specs={s} lensSize={color?.size} rows={[
            ['Brand', p.brand], ['Material', p.material], ['Shape', p.shape],
            ['Frame Type', p.frame_type], ['Gender', p.gender],
            ['Colour', color?.color], ['Size', color?.size && `${color.size} mm`],
            ['Bridge Size', s.bridge_size && `${s.bridge_size} mm`], ['Temple Length', s.temple_length && `${s.temple_length} mm`],
            ['Rim Height', s.rim_height && `${s.rim_height} mm`], ['Frame Weight', s.frame_weight],
            ['HSN Code', s.hsn_code], ['MOQ', s.min_qty && `${s.min_qty} Piece`],
          ]} />
        </div>
      </div>

    </div>

    {/* related — its own wider (1600, not 1200) container so these tiles get
        the same "grow with the monitor" treatment as the Shop grid and Home
        collections, independent of the narrower reading-width column the
        gallery/info section above uses. Fixed 5-column grid (.sf-related-grid,
        defined in StoreLayout) so a full set reads as two even 5+5 rows that
        fill the screen width, rather than a variable auto-fit column count.
        Backend sends up to 10; trimmed to a multiple of 5 here so a category
        with, say, 8 matches never leaves a lone card stranded on its own row. */}
    {related.length > 0 && (
      <section style={{ maxWidth: 1600, margin: '44px auto 0', padding: '0 24px 24px' }}>
        <h2 style={{ fontSize: 19, fontWeight: 600, marginBottom: 16 }}>Other Recommendations For Your Business</h2>
        <div className="sf-related-grid" style={{ display: 'grid', gap: 16 }}>
          {(related.length > 5 ? related.slice(0, Math.floor(related.length / 5) * 5) : related).map(r => <ProductCard key={r.model_no} p={r} />)}
        </div>
      </section>
    )}
    </>
  );
}

// Frame Measurements diagram + the attribute table share one card — spec
// rows sit directly under the diagram instead of floating as a separate
// block, so the two read as one "Specifications" section, not two.
// "Lens" is the frame size (color.size, e.g. 52mm) — the first number in the
// standard Lens-Bridge-Temple eyewear notation (e.g. 52-18-140).
function Specifications({ specs, lensSize, rows }) {
  const { bridge_size, temple_length, rim_height } = specs;
  const hasDiagram = !!(bridge_size || temple_length || rim_height || lensSize);
  const specRows = rows.filter(r => r[1]);
  if (!hasDiagram && !specRows.length) return null;

  return (
    <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 10, padding: 16, marginTop: 18 }}>
      {hasDiagram && (
        <>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 12, color: '#555' }}>Frame Measurements</div>
          <svg viewBox="0 0 340 120" style={{ width: '100%', maxWidth: 340, display: 'block' }}>
            {/* left lens */}
            <rect x="20" y="30" width="110" height="60" rx="26" fill="#fff" stroke="#191919" strokeWidth="2.5" />
            {/* bridge */}
            <path d="M130 50 q30 -12 60 0" fill="none" stroke="#191919" strokeWidth="2.5" />
            {/* right lens */}
            <rect x="190" y="30" width="110" height="60" rx="26" fill="#fff" stroke="#191919" strokeWidth="2.5" />
            {/* temple */}
            <line x1="300" y1="42" x2="335" y2="36" stroke="#191919" strokeWidth="2.5" />
            {/* labels */}
            {lensSize && <text x="75" y="24" fontSize="10" fill="#85cde9" textAnchor="middle" fontWeight="bold">{lensSize}mm</text>}
            {bridge_size && <text x="160" y="24" fontSize="10" fill="#85cde9" textAnchor="middle" fontWeight="bold">{bridge_size}mm</text>}
            {rim_height && <text x="10" y="63" fontSize="10" fill="#85cde9" textAnchor="middle" fontWeight="bold" transform="rotate(-90 10 63)">{rim_height}mm</text>}
            {temple_length && <text x="318" y="30" fontSize="10" fill="#85cde9" textAnchor="middle" fontWeight="bold">{temple_length}mm</text>}
            <text x="75" y="105" fontSize="9" fill="#999" textAnchor="middle">Lens</text>
            <text x="160" y="105" fontSize="9" fill="#999" textAnchor="middle">Bridge</text>
          </svg>
        </>
      )}
      {specRows.length > 0 && (() => {
        // Split into two even halves — the first column takes the extra row
        // on an odd count (13 rows -> 7 + 6), matching how a print spec
        // sheet would balance two columns.
        const half = Math.ceil(specRows.length / 2);
        const col1 = specRows.slice(0, half);
        const col2 = specRows.slice(half);
        return (
          <>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 12, marginTop: hasDiagram ? 18 : 0, color: '#555' }}>Product Attributes</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 24px' }}>
              <SpecTable rows={col1} />
              {col2.length > 0 && <SpecTable rows={col2} />}
            </div>
          </>
        );
      })()}
    </div>
  );
}

// One half of the split attribute table — flex-basis lets it sit side by
// side with its twin when the card is wide enough, and drop to a single
// stacked column on narrow screens instead of squeezing.
function SpecTable({ rows }) {
  return (
    <table style={{ flex: '1 1 240px', minWidth: 200, borderCollapse: 'collapse', fontSize: 13 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k} style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '8px 10px 8px 0', color: '#888', width: 120 }}>{k}</td>
            <td style={{ padding: '8px 0', fontWeight: 500 }}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const bc = { color: '#999', textDecoration: 'none' };
const enqInp = { padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 13.5, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
